import { Button } from "antd";
import { useGetCommissionQuery } from "api/TenantSlug/Commission/hooks";
import { ChildCommissionsSection } from "components/Drawers/EditCommissionDrawer/ChildCommissionsSection.tsx";
import { ManageCommissionForm } from "components/Forms/ManageCommissionForm/ManageCommissionForm";
import { Drawer } from "components/UI/Drawer";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { CommissionType } from "types/enums.ts";
import { DrawerId, FormId, FormType } from "types/models.ts";
import { getDrawerSearchParams } from "utils/drawerHelpers";

interface EditCommissionDrawerProps {
    commissionId: string;
    onSubmit: () => void;
}

export const EditCommissionDrawer: FC<EditCommissionDrawerProps> = ({ commissionId, onSubmit }) => {
    const { t } = usePaldockTranslation("drawers.edit-commission");
    const { t: tC } = usePaldockTranslation("common");
    const drawerId = DrawerId.EditCommission;
    const formId = FormId.ManageCommissionForm;

    const drawerUtils = useDrawer();
    const getCommissionQuery = useGetCommissionQuery(commissionId, {
        filter: {
            include: "parent,offer",
        },
    });

    const handleSubmit = async () => {
        onSubmit();
        drawerUtils.hide(drawerId, getDrawerSearchParams(drawerId, "id"));
        await getCommissionQuery.refetch();
    };

    if (!getCommissionQuery.data?.commission) {
        return <></>;
    }

    const commission = getCommissionQuery.data?.commission;

    return (
        <Drawer
            drawerId={drawerId}
            title={
                <DrawerTitle
                    mainTitle={t("title")}
                    subTitles={[commission.payout_placeholder ?? commission.payout_formula]}
                />
            }
            searchParamsToClean={getDrawerSearchParams(drawerId, "id")}
            extraButtons={
                <Button size={"large"} htmlType="submit" type={"primary"} form={formId}>
                    {tC("save")}
                </Button>
            }
        >
            <DrawerContent>
                <ManageCommissionForm
                    commission={getCommissionQuery.data?.commission}
                    formId={formId}
                    onSubmit={handleSubmit}
                    formType={FormType.Edit}
                />
                {commission.commission_type === CommissionType.Tenant && (
                    <ChildCommissionsSection
                        parentCommissionId={commission.id}
                        commission={commission}
                        onRefetchTable={onSubmit}
                        onRefetchParent={() => getCommissionQuery.refetch()}
                    />
                )}
            </DrawerContent>
        </Drawer>
    );
};
